import React, { useEffect, useState } from "react";
import { Row, Col, Card, CardBody, Badge, Spinner } from "reactstrap";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { useAccount } from "../../context/AccountContext";
import { useAuth } from "../../context/AuthContext";
import PageTitleAlt2 from "../../Layout/AppMain/PageTitleAlt2";
import { formatCurrency, formatCurrencySigned } from "../../utils/currency";

/* ── helpers ── */
// `fmt` returns just the currency-formatted magnitude (no sign) — callers that
// already render their own "$" prefix keep doing so via fmt; callers that want
// the full "$1,234"/"+$1,234" string should use formatCurrency/pnlFmt instead.
const fmt = (n, currencySymbol) =>
  n == null ? "—" : formatCurrency(Math.abs(n), currencySymbol, 0);

const pnlColor = (v) => (v >= 0 ? "#28a745" : "#dc3545");

const pnlFmt = (v, currencySymbol) =>
  v == null ? "—" : formatCurrencySigned(v, currencySymbol, 0);

/* ── subscription validity card ── */
const SubscriptionCard = ({ user }) => {
  if (!user) return null;
  const plan = user.subscription_plan || "retail";
  const planLabel = { retail: "Retail", pro: "Pro", advisor: "Advisor" }[plan] || plan;
  const planColor = { retail: "#fd7e14", pro: "#007bff", advisor: "#28a745" }[plan] || "#6c757d";

  let daysLeft = null;
  let dateLabel = "—";
  let daysColor = "#28a745";
  let daysText  = "";

  if (user.validity_date) {
    const today    = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry   = new Date(user.validity_date);
    expiry.setHours(0, 0, 0, 0);
    daysLeft  = Math.floor((expiry - today) / 86400000);
    dateLabel = expiry.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
    if (daysLeft < 0)      { daysColor = "#dc3545"; daysText = "Expired"; }
    else if (daysLeft <= 7){ daysColor = "#dc3545"; daysText = daysLeft === 0 ? "Expires today!" : `${daysLeft}d left`; }
    else if (daysLeft <= 30){ daysColor = "#fd7e14"; daysText = `${daysLeft}d left`; }
    else                   { daysColor = "#28a745"; daysText = `${daysLeft}d left`; }
  }

  return (
    <Card className="mb-3" style={{ borderRadius: 12, border: "1px solid #eee" }}>
      <CardBody style={{ padding: "14px 20px" }}>
        <div style={{ fontSize: 11, color: "#888", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>
          Subscription
        </div>
        <div style={{ display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap" }}>
          <div>
            <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 10, background: `${planColor}20`, color: planColor, fontWeight: 700, fontSize: 13 }}>
              {planLabel}
            </span>
          </div>
          <div style={{ fontSize: 13, color: "#555" }}>
            <span style={{ color: "#888" }}>Valid until: </span>
            <span style={{ fontWeight: 600 }}>{dateLabel}</span>
          </div>
          {daysText && (
            <div style={{ fontSize: 13, fontWeight: 700, color: daysColor }}>
              {daysText}
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

/* ── single metric card ── */
const MetricCard = ({ label, value, sub, color, highlight }) => (
  <Card className="mb-3" style={{ borderRadius: 12, border: "1px solid #eee" }} data-highlight={highlight}>
    <CardBody style={{ padding: "18px 20px" }}>
      <div style={{ fontSize: 11, color: "#888", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || "#333" }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "#aaa", marginTop: 4 }}>{sub}</div>}
    </CardBody>
  </Card>
);

/* ── Potential P&L card with per-expiry breakdown ── */
const PotentialPnLCard = ({ option_metrics, currencySymbol }) => {
  const breakdown = option_metrics.expiry_breakdown || [];

  const fmtUsd    = (v) => (v == null ? "—" : formatCurrency(Math.abs(v), currencySymbol, 0));
  const fmtSigned = (v) => (v == null ? "—" : formatCurrencySigned(v, currencySymbol, 0));
  const signColor = (v) => (v == null ? "#17a2b8" : v < 0 ? "#dc3545" : "#17a2b8");

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Group weekly expiries into calendar months, sum all figures per month
  const monthMap = {};
  breakdown.forEach(row => {
    const exp = row.expiry_date ? new Date(row.expiry_date) : null;
    if (!exp) return;
    const key = `${exp.getFullYear()}-${String(exp.getMonth() + 1).padStart(2, '0')}`;
    if (!monthMap[key]) monthMap[key] = [];
    monthMap[key].push({ ...row, _exp: exp });
  });

  const allRows = Object.entries(monthMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, items]) => {
      const lastExp    = items[items.length - 1]._exp;
      const dte        = Math.round((lastExp - today) / 86400000);
      const mon        = lastExp.toLocaleDateString("en-US", { day: "numeric", month: "short" });
      const open       = items.reduce((s, r) => s + (r.open_premium   || 0), 0);
      const booked     = items.reduce((s, r) => s + (r.booked_premium || 0), 0);
      const stock      = items.reduce((s, r) => s + (r.stock_potential || 0), 0);
      const cycleTotal = open + booked + stock;
      const hasBooked  = Math.abs(booked) >= 1;
      return { mon, dte, open, booked, stock, cycleTotal, hasBooked };
    });

  // Show months whose last (monthly) expiry hasn't passed yet, max 2 (current + next)
  const rows = allRows.filter(r => r.dte >= 0).slice(0, 2);

  // Headline = current month total; next month shown as context below
  const current = rows[0] || null;
  const next    = rows[1] || null;

  const dteChip = (dte, urgent) => {
    if (dte == null) return null;
    const color = dte <= 7 ? "#dc3545" : dte <= 15 ? "#fd7e14" : "#17a2b8";
    return (
      <span style={{
        display: "inline-block", fontSize: 10, fontWeight: 700,
        background: color + "22", color, borderRadius: 4,
        padding: "1px 5px", marginLeft: 4, verticalAlign: "middle",
      }}>
        {dte}d
      </span>
    );
  };

  return (
    <Card className="mb-3" style={{ borderRadius: 12, border: "1px solid #eee" }} data-highlight="potential-pnl">
      <CardBody style={{ padding: "18px 20px" }}>
        {/* Header label */}
        <div style={{ fontSize: 11, color: "#888", marginBottom: 2, textTransform: "uppercase", letterSpacing: "0.5px" }}>
          Potential P&amp;L
        </div>

        {current ? (
          <>
            {/* Headline = current expiry total */}
            <div style={{ fontSize: 22, fontWeight: 700, color: signColor(current.cycleTotal), marginBottom: 2 }}>
              {fmtSigned(current.cycleTotal)}
            </div>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 10 }}>
              {current.mon} expiry{dteChip(current.dte)}
              {current.hasBooked && (
                <span style={{ marginLeft: 8 }}>
                  Open <span style={{ color: current.open >= 0 ? "#17a2b8" : "#dc3545", fontWeight: 600 }}>{fmtSigned(current.open)}</span>
                  {"  ·  "}Booked <span style={{ color: current.booked >= 0 ? "#28a745" : "#dc3545", fontWeight: 600 }}>{fmtSigned(current.booked)}</span>
                </span>
              )}
              {!current.hasBooked && current.open !== 0 && (
                <span style={{ marginLeft: 8 }}>
                  Open <span style={{ color: current.open >= 0 ? "#17a2b8" : "#dc3545", fontWeight: 600 }}>{fmtSigned(current.open)}</span>
                </span>
              )}
              {current.stock > 0 && (
                <span style={{ marginLeft: 8 }}>
                  {"  ·  "}Stock <span style={{ color: "#28a745", fontWeight: 600 }}>+{fmtUsd(current.stock)}</span>
                </span>
              )}
            </div>

            {/* Next expiry — secondary row */}
            {next && (
              <div style={{
                borderTop: "1px solid #f0f0f0", paddingTop: 8,
                display: "grid", gridTemplateColumns: "auto 1fr auto",
                gap: "0 8px", alignItems: "baseline", fontSize: 12,
              }}>
                <span style={{ fontWeight: 600, color: "#999", whiteSpace: "nowrap" }}>
                  Next · {next.mon}{dteChip(next.dte)}
                </span>
                <span style={{ color: "#aaa", fontSize: 11 }}>
                  {next.open !== 0 && <>Open: <span style={{ color: next.open >= 0 ? "#17a2b8" : "#dc3545", fontWeight: 600 }}>{fmtSigned(next.open)}</span></>}
                  {next.hasBooked && (
                    <>
                      {next.open !== 0 && "  ·  "}
                      Booked: <span style={{ color: next.booked >= 0 ? "#28a745" : "#dc3545", fontWeight: 600 }}>{fmtSigned(next.booked)}</span>
                    </>
                  )}
                  {next.stock > 0 && (
                    <>{"  ·  "}Stock: <span style={{ color: "#28a745", fontWeight: 600 }}>+{fmtUsd(next.stock)}</span></>
                  )}
                </span>
                <span style={{ fontWeight: 700, color: signColor(next.cycleTotal), textAlign: "right", whiteSpace: "nowrap" }}>
                  {fmtSigned(next.cycleTotal)}
                </span>
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: 12, color: "#aaa", marginTop: 6 }}>No active option positions</div>
        )}
      </CardBody>
    </Card>
  );
};

/* ── stat pill ── */
const Pill = ({ label, value, color, onClick, highlight }) => (
  <div
    onClick={onClick}
    data-highlight={highlight}
    style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      background: "#fff", border: "1px solid #eee", borderRadius: 10,
      padding: "12px 18px", cursor: onClick ? "pointer" : "default", minWidth: 100,
    }}
  >
    <span style={{ fontSize: 20, fontWeight: 700, color: color || "#333" }}>{value}</span>
    <span style={{ fontSize: 11, color: "#888", marginTop: 2, textAlign: "center" }}>{label}</span>
  </div>
);

/* ═══════════════════════════════════════════════
   SINGLE ACCOUNT VIEW
═══════════════════════════════════════════════ */
const SingleAccountView = ({ account }) => {
  const history = useHistory();
  const { currencySymbol } = useAccount();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(null);
  const [roi, setRoi]         = useState(null);
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    if (!account) return;
    setLoading(true);
    axios.get("/dashboard/summary", { params: { username: account } })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
    axios.get("/dashboard/balance", { params: { username: account } })
      .then(r => setBalance(r.data))
      .catch(() => setBalance(null));
    axios.get("/dashboard/roi", { params: { username: account } })
      .then(r => setRoi(r.data.error ? null : r.data))
      .catch(() => setRoi(null));
    axios.get("/credits/status", { params: { username: account } })
      .then(r => setCredits(r.data.status === "not_tracked" ? null : r.data))
      .catch(() => setCredits(null));
  }, [account]);

  if (loading) return <div style={{ textAlign: "center", padding: 60 }}><Spinner color="primary" /></div>;
  if (!data) return null;

  const { option_metrics, strategies, rules, totals } = data;
  const totalPnl        = totals.stock_pnl + option_metrics.fo_unrealised_pnl;
  const activeStrategies = strategies["ACTIVE"] || 0;

  return (
    <div style={{ padding: "0 25px 30px" }}>
      {/* Account badge */}
      <div style={{ marginBottom: 20 }}>
        <Badge color="secondary" pill style={{ fontSize: 13, padding: "5px 12px" }}>
          {data.account.username}
        </Badge>
        <span style={{ marginLeft: 8, color: "#888", fontSize: 13 }}>{data.account.broker}</span>
      </div>

      {/* Trading balance strip */}
      <Row className="mb-3">
        {balance && balance.available_cash != null ? (
          <>
            <Col md={3}>
              <MetricCard
                label="Available Cash"
                value={fmt(balance.available_cash, currencySymbol)}
                color="#28a745"
                sub={balance.is_stale ? `Last known · ${new Date(balance.cached_at * 1000).toLocaleTimeString()}` : null}
              />
            </Col>
            <Col md={3}>
              <MetricCard
                label="Used Margin"
                value={fmt(balance.used_margin, currencySymbol)}
                color="#dc3545"
                sub={balance.is_stale ? "Stale data" : null}
              />
            </Col>
            <Col md={3}>
              <MetricCard
                label="Total Margin"
                value={fmt(balance.total_margin, currencySymbol)}
                color={balance.is_stale ? "#888" : "#495057"}
                sub={balance.is_stale ? "Broker offline" : null}
              />
            </Col>
            {roi && roi.roi_pct != null && (
              <Col md={3}>
                <MetricCard
                  label={`Monthly ROI${roi.is_partial ? " (to date)" : ""}`}
                  value={`${roi.roi_pct >= 0 ? "+" : ""}${roi.roi_pct}%`}
                  color={pnlColor(roi.roi_pct)}
                />
              </Col>
            )}
            {balance.stock_margin != null && balance.option_margin != null && (
              <>
                <Col md={3}>
                  <MetricCard label="Stock Margin" value={fmt(balance.stock_margin, currencySymbol)} color="#6f42c1" />
                </Col>
                <Col md={3}>
                  <MetricCard label="Option Margin" value={fmt(balance.option_margin, currencySymbol)} color="#fd7e14" />
                </Col>
              </>
            )}
          </>
        ) : (
          <Col>
            <Card className="mb-3" style={{ borderRadius: 12, border: "1px solid #eee", background: "#fafafa" }}>
              <CardBody style={{ padding: "18px 20px" }}>
                <div style={{ fontSize: 12, color: "#aaa" }}>Trading balance unavailable (broker offline or session expired)</div>
              </CardBody>
            </Card>
          </Col>
        )}
      </Row>

      {/* P&L cards */}
      <Row>
        <Col md={3}>
          <MetricCard
            label="Total P&L (Stock + Options)"
            highlight="total-pnl"
            value={pnlFmt(totalPnl, currencySymbol)}
            color={pnlColor(totalPnl)}
            sub="Unrealized combined"
          />
        </Col>
        <Col md={3}>
          <MetricCard
            label="Stock Portfolio P&L"
            highlight="stock-pnl"
            value={pnlFmt(totals.stock_pnl, currencySymbol)}
            color={pnlColor(totals.stock_pnl)}
            sub={`Invested ${fmt(totals.total_invested, currencySymbol)}`}
          />
        </Col>
        <Col md={3}>
          <MetricCard
            label="F&O Unrealized P&L"
            highlight="option-pnl"
            value={pnlFmt(option_metrics.fo_unrealised_pnl, currencySymbol)}
            color={pnlColor(option_metrics.fo_unrealised_pnl)}
            sub={`${option_metrics.option_positions} open positions`}
          />
        </Col>
        <Col md={3}>
          <PotentialPnLCard option_metrics={option_metrics} currencySymbol={currencySymbol} />
        </Col>
      </Row>

      {/* Stat pills */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 28 }}>
        <Pill
          label="Stock Positions"
          highlight="stock-positions"
          value={totals.stock_count}
          onClick={() => history.push("/trading")}
        />
        <Pill
          label="Short Option Value"
          highlight="short-option-value"
          value={fmt(option_metrics.short_option_value, currencySymbol)}
          color="#6f42c1"
        />
        <Pill
          label="Active Strategies"
          highlight="active-strategies"
          value={activeStrategies}
          color={activeStrategies > 0 ? "#28a745" : "#aaa"}
          onClick={() => history.push("/strategy/manage")}
        />
        <Pill
          label="Active Rules"
          highlight="active-rules"
          value={rules.active}
          color={rules.active > 0 ? "#007bff" : "#aaa"}
          onClick={() => history.push("/rules/builder")}
        />
        {credits && (
          <Pill
            label="Capacity Credits"
            highlight="capacity-credits"
            value={credits.balance}
            color={credits.status === "exhausted" ? "#dc3545" : credits.status === "low" ? "#fd7e14" : "#28a745"}
          />
        )}
      </div>

      {/* Quick actions */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {[
          { label: "Build Strategy",    path: "/strategy/build",    color: "#007bff", testid: "quick-action-build-strategy" },
          { label: "Manage Strategies", path: "/strategy/manage",   color: "#6c757d", testid: "quick-action-manage-strategies" },
          { label: "Rule Builder",      path: "/rules/builder",     color: "#6f42c1", testid: "quick-action-rule-builder" },
          { label: "View Positions",    path: "/trading/positions", color: "#28a745", testid: "quick-action-view-positions" },
        ].map(b => (
          <button
            key={b.path}
            data-testid={b.testid}
            onClick={() => history.push(b.path)}
            style={{
              padding: "7px 16px", borderRadius: 8, border: "none",
              background: b.color, color: "#fff", fontSize: 13, cursor: "pointer",
            }}
          >
            {b.label}
          </button>
        ))}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   ALL ACCOUNTS VIEW
═══════════════════════════════════════════════ */
const AllAccountsView = ({ onSelectAccount }) => {
  const history = useHistory();
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading]     = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get("/dashboard/all-summary")
      .then(r => setSummaries(r.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: "center", padding: 60 }}><Spinner color="primary" /></div>;

  if (summaries.length === 0)
    return <div style={{ padding: 40, color: "#888", textAlign: "center" }}>No accounts found.</div>;

  return (
    <div style={{ padding: "0 25px 30px" }}>

      {/* Aggregate totals strip */}
      {(() => {
        const totalStockPnl  = summaries.reduce((a, s) => a + s.stock_pnl, 0);
        const totalOptionPnl = summaries.reduce((a, s) => a + s.fo_unrealised_pnl, 0);
        const totalPotential = summaries.reduce((a, s) => a + s.potential_pnl, 0);
        const totalShort     = summaries.reduce((a, s) => a + s.short_option_value, 0);
        const totalCash      = summaries.reduce((a, s) => a + (s.available_cash || 0), 0);

        return (
          <Row className="mb-4">
            {[
              { label: "Total Available Cash", value: fmt(totalCash), color: "#28a745" },
              { label: "Combined P&L",      value: pnlFmt(totalStockPnl + totalOptionPnl), color: pnlColor(totalStockPnl + totalOptionPnl) },
              { label: "Total Stock P&L",   value: pnlFmt(totalStockPnl),  color: pnlColor(totalStockPnl) },
              { label: "Total F&O P&L",     value: pnlFmt(totalOptionPnl), color: pnlColor(totalOptionPnl) },
              { label: "Total Potential P&L", value: `+${fmt(totalPotential)}`, color: "#17a2b8" },
              { label: "Short Option Value",  value: fmt(totalShort),       color: "#6f42c1" },
            ].map(c => (
              <Col key={c.label}>
                <Card className="mb-2" style={{ borderRadius: 10, border: "1px solid #eee", textAlign: "center" }}>
                  <CardBody style={{ padding: "12px 10px" }}>
                    <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", marginBottom: 4 }}>{c.label}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: c.color }}>{c.value}</div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        );
      })()}

      <div style={{ fontSize: 12, color: "#888", marginBottom: 16 }}>
        {summaries.length} account{summaries.length !== 1 ? "s" : ""} — click a card to view account details
      </div>

      {/* Per-account cards */}
      <Row>
        {summaries.map(s => {
          const totalPnl = s.stock_pnl + s.fo_unrealised_pnl;
          return (
            <Col md={6} xl={4} key={s.username} className="mb-3">
              <Card
                style={{
                  borderRadius: 14, border: "1px solid #eee", cursor: "pointer",
                  transition: "box-shadow 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.10)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                onClick={() => onSelectAccount(s.username)}
              >
                <CardBody style={{ padding: "18px 20px" }}>
                  {/* Header */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>{s.username}</div>
                      <div style={{ fontSize: 12, color: "#888" }}>{s.broker}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: pnlColor(totalPnl) }}>
                        {pnlFmt(totalPnl)}
                      </div>
                      <div style={{ fontSize: 11, color: "#aaa" }}>Total P&L</div>
                    </div>
                  </div>

                  {/* Metrics grid */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 12px", fontSize: 12 }}>
                    <div>
                      <span style={{ color: "#888" }}>Stock P&L</span><br />
                      <span style={{ fontWeight: 600, color: pnlColor(s.stock_pnl) }}>{pnlFmt(s.stock_pnl)}</span>
                    </div>
                    <div>
                      <span style={{ color: "#888" }}>F&O P&L</span><br />
                      <span style={{ fontWeight: 600, color: pnlColor(s.fo_unrealised_pnl) }}>{pnlFmt(s.fo_unrealised_pnl)}</span>
                    </div>
                    <div>
                      <span style={{ color: "#888" }}>Potential P&L</span><br />
                      <span style={{ fontWeight: 600, color: "#17a2b8" }}>+{fmt(s.potential_pnl)}</span>
                    </div>
                    <div>
                      <span style={{ color: "#888" }}>Short Value</span><br />
                      <span style={{ fontWeight: 600, color: "#6f42c1" }}>{fmt(s.short_option_value)}</span>
                    </div>
                    {s.available_cash != null && (
                      <div>
                        <span style={{ color: "#888" }}>Available Cash</span><br />
                        <span style={{ fontWeight: 600, color: "#28a745" }}>{fmt(s.available_cash)}</span>
                      </div>
                    )}
                    {s.used_margin != null && (
                      <div>
                        <span style={{ color: "#888" }}>Used Margin</span><br />
                        <span style={{ fontWeight: 600, color: "#dc3545" }}>{fmt(s.used_margin)}</span>
                      </div>
                    )}
                  </div>

                  {/* Footer badges */}
                  <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                    <Badge color="light" style={{ color: "#555", border: "1px solid #ddd", fontSize: 11 }}>
                      {s.stock_count} stocks
                    </Badge>
                    <Badge color="light" style={{ color: "#555", border: "1px solid #ddd", fontSize: 11 }}>
                      {s.option_positions} options
                    </Badge>
                    {s.active_strategies > 0 && (
                      <Badge color="success" style={{ fontSize: 11 }}>
                        {s.active_strategies} strategy active
                      </Badge>
                    )}
                    {s.active_rules > 0 && (
                      <Badge color="primary" style={{ fontSize: 11 }}>
                        {s.active_rules} rules
                      </Badge>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   ROOT COMPONENT
═══════════════════════════════════════════════ */
const AccountDashboard = () => {
  const { selectedAccount, setSelectedAccount } = useAccount();
  const { user } = useAuth();
  const isAll = selectedAccount === "All Accounts";

  return (
    <>
      <PageTitleAlt2
        heading="Portfolio Dashboard"
        subheading={isAll ? "Overview across all accounts" : "Account-level performance summary"}
        icon="pe-7s-graph icon-gradient bg-ripe-malin"
      />
      <div style={{ padding: "0 25px 4px" }}>
        <SubscriptionCard user={user} />
      </div>
      {isAll
        ? <AllAccountsView onSelectAccount={setSelectedAccount} />
        : <SingleAccountView account={selectedAccount} />
      }
    </>
  );
};

export default AccountDashboard;
